import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteFeature } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { FeatureModel } from '../../../../../types';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { Messages } from '../../../../utils/message-constants';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteFeatureDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedFeature: FeatureModel;
  changeLogRoles: string[];
  setSelectedFeature: Dispatch<SetStateAction<FeatureModel | null>>;
}

export default function DeleteFeatureDialog({
  open,
  handleClose,
  selectedFeature,
  changeLogRoles,
  setSelectedFeature,
}: DeleteFeatureDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteFeature(selectedFeature, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Deleted('Feature'),
      });

      queryClient.setQueryData<FeatureModel[] | undefined>(
        [UserKeys.Features],
        (oldData) => {
          if (oldData) {
            return oldData.filter(
              (feature) => feature.etags !== selectedFeature.etags
            );
          }
        }
      );

      setChangeLogMessage('');
      handleClose();
      setSelectedFeature(null);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting('this feature'),
      });
    },
    onSettled: () => {
      createEvent(analytics.calendars.resource_calendars.delete_feature, {
        module: ModuleList.modules.calendars.name,
        section: ModuleList.modules.calendars.sections.resourceCalendars,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, CalendarRoles.DeleteFeatures.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        setSelectedFeature(null);
        handleClose();
      }}
      title="Delete Feature"
      dialogContent={
        <Box>
          <Typography>
            Delete <strong>{selectedFeature.name}</strong>?
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Delete Feature',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
